import React, { useState, useEffect } from 'react';
import { MapPin, Star, Clock, Users, DollarSign, TrendingUp, Search, Download, Plus, Eye, Edit, Trash2, BarChart3, Zap, Globe, Calendar, Coffee, Waves, Car, Music, Utensils } from 'lucide-react';

const DestinationKnowledge = () => {
  const [selectedDestination, setSelectedDestination] = useState('cannes');
  const [activeTab, setActiveTab] = useState('restaurants');
  const [showAddForm, setShowAddForm] = useState(false);

  // Destinations disponibles
  const destinations = [
    {
      id: 'cannes',
      name: 'Cannes',
      location: 'Côte d\'Azur, France',
      entries: 156,
      rating: 4.8,
      lastUpdate: '2024-09-12',
      image: 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=300&h=200&fit=crop&crop=center'
    },
    {
      id: 'saint-tropez',
      name: 'Saint-Tropez',
      location: 'Var, France',
      entries: 134,
      rating: 4.9,
      lastUpdate: '2024-09-10',
      image: 'https://images.unsplash.com/photo-1577717903315-1691ae25ab3f?w=300&h=200&fit=crop&crop=center'
    },
    {
      id: 'monaco',
      name: 'Monaco',
      location: 'Principauté de Monaco',
      entries: 98,
      rating: 4.7,
      lastUpdate: '2024-09-11',
      image: 'https://images.unsplash.com/photo-1596394516093-501ba68a0ba6?w=300&h=200&fit=crop&crop=center'
    },
    {
      id: 'nice',
      name: 'Nice',
      location: 'Alpes-Maritimes, France',
      entries: 187,
      rating: 4.6,
      lastUpdate: '2024-09-09',
      image: 'https://images.unsplash.com/photo-1539650116574-75c0c6d68146?w=300&h=200&fit=crop&crop=center'
    }
  ];

  // Base de connaissances par destination
  const knowledgeBase = {
    cannes: {
      restaurants: [
        {
          id: 1,
          name: 'La Palme d\'Or',
          type: 'Gastronomique',
          category: 'Premium',
          rating: 4.9,
          priceRange: '€€€€',
          location: 'Hôtel Martinez',
          specialties: 'Cuisine méditerranéenne créative',
          clientReviews: 'Excellente expérience culinaire, service impeccable',
          ourNotes: 'Réservation obligatoire, demander table avec vue mer',
          tags: ['Étoilé Michelin', 'Vue mer', 'Romantique']
        },
        {
          id: 2,
          name: 'Le Comptoir du Marché',
          type: 'Bistrot',
          category: 'Recommandé',
          rating: 4.3,
          priceRange: '€€',
          location: 'Marché Forville',
          specialties: 'Cuisine provençale traditionnelle',
          clientReviews: 'Ambiance authentique, produits frais du marché',
          ourNotes: 'Parfait pour déjeuner, terrasse agréable',
          tags: ['Local', 'Authentique', 'Marché']
        },
        {
          id: 3,
          name: 'Chez Vincent',
          type: 'Poissonnerie',
          category: 'À Éviter',
          rating: 2.1,
          priceRange: '€€€',
          location: 'Vieux Port',
          specialties: 'Fruits de mer',
          clientReviews: 'Service décevant, qualité variable',
          ourNotes: 'Plusieurs clients insatisfaits, éviter en haute saison',
          tags: ['Touristique', 'Overpriced']
        }
      ],
      beaches: [
        {
          id: 1,
          name: 'Plage du Martinez',
          type: 'Privée',
          category: 'Premium',
          rating: 4.8,
          priceRange: '€€€€',
          location: 'Croisette',
          specialties: 'Service de luxe, restaurant',
          clientReviews: 'Plage exclusive, service parfait',
          ourNotes: 'Réservation longtan recommandée, transats premium',
          tags: ['Luxe', 'Service', 'Croisette']
        },
        {
          id: 2,
          name: 'Plage de la Bocca',
          type: 'Publique',
          category: 'Recommandé',
          rating: 4.2,
          priceRange: '€',
          location: 'La Bocca',
          specialties: 'Familiale, moins fréquentée',
          clientReviews: 'Calme et agréable, bon pour familles',
          ourNotes: 'Alternative gratuite, parking disponible',
          tags: ['Familiale', 'Gratuite', 'Calme']
        }
      ],
      activities: [
        {
          id: 1,
          name: 'Îles de Lérins',
          type: 'Excursion',
          category: 'Premium',
          rating: 4.7,
          priceRange: '€€€',
          location: 'Au large de Cannes',
          specialties: 'Nature, monastère, plages sauvages',
          clientReviews: 'Excursion magnifique, dépaysement total',
          ourNotes: 'Prévoir journée complète, déjeuner sur place',
          tags: ['Nature', 'Culture', 'Bateau']
        }
      ],
      yachtRoutes: [
        {
          id: 1,
          name: 'Circuit Îles d\'Or',
          duration: '8 heures',
          distance: '45 nm',
          highlights: ['Porquerolles', 'Port-Cros', 'Île du Levant'],
          difficulty: 'Facile',
          bestTime: 'Mai à Septembre'
        }
      ]
    }
  };

  // Suggestions IA
  const aiSuggestions = [
    {
      type: 'opportunity',
      title: 'Nouveau restaurant tendance détecté',
      content: 'Le restaurant "La Table Azur" gagne en popularité sur TripAdvisor (+200% mentions cette semaine)',
      action: 'Ajouter à la base'
    },
    {
      type: 'trend',
      title: 'Tendance "Cuisine végane"',
      content: '15% des clients UHNW demandent des options véganes dans leurs préférences',
      action: 'Enrichir catégorie'
    },
    {
      type: 'urgent',
      title: 'Mise à jour requise',
      content: 'La Villa Ephrussi a changé ses horaires d\'ouverture (fermée le lundi)',
      action: 'Mettre à jour'
    }
  ];

  const currentDestination = destinations.find(d => d.id === selectedDestination);
  const currentKnowledge = knowledgeBase[selectedDestination] || {};
  const currentEntries = currentKnowledge[activeTab] || [];

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Premium': return 'bg-yellow-100 text-yellow-800';
      case 'Recommandé': return 'bg-green-100 text-green-800';
      case 'À Éviter': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Premium': return <Star className="text-yellow-600" size={16} />;
      case 'Recommandé': return <TrendingUp className="text-green-600" size={16} />;
      case 'À Éviter': return <div className="w-4 h-4 bg-red-600 rounded-full"></div>;
      default: return <div className="w-4 h-4 bg-gray-400 rounded-full"></div>;
    }
  };

  const getTabIcon = (tabId) => {
    switch (tabId) {
      case 'restaurants': return <Utensils size={18} />;
      case 'beaches': return <Waves size={18} />;
      case 'activities': return <Music size={18} />;
      case 'shopping': return <Coffee size={18} />;
      case 'culture': return <Calendar size={18} />;
      case 'transport': return <Car size={18} />;
      case 'yachtRoutes': return <Globe size={18} />;
      default: return <MapPin size={18} />;
    }
  };

  const AddEntryForm = () => {
    if (!showAddForm) return null;

    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <h4 className="font-medium text-blue-800 mb-3">Ajout Rapide - {activeTab}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <input
              type="text"
              placeholder="Nom de l'établissement"
              className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <select className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
              <option>Catégorie</option>
              <option>Premium</option>
              <option>Recommandé</option>
              <option>Standard</option>
              <option>À Éviter</option>
            </select>
          </div>
          <div>
            <input
              type="text"
              placeholder="Localisation"
              className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <input
              type="text"
              placeholder="Spécialités"
              className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="md:col-span-2">
            <textarea
              placeholder="Notes internes..."
              className="w-full px-3 py-2 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="2"
            ></textarea>
          </div>
        </div>
        <div className="flex justify-end space-x-2 mt-4">
          <button
            onClick={() => setShowAddForm(false)}
            className="px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50"
          >
            Annuler
          </button>
          <button
            onClick={() => {
              // Logique de sauvegarde
              setShowAddForm(false);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Ajouter
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="destination-knowledge">
      {/* En-tête */}
      <div className="page-header bg-white border-b border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Base de Connaissances Destinations</h1>
            <p className="text-gray-600 mt-1">Guide complet des destinations et recommandations pour vos clients VIP</p>
          </div>
          
          <div className="flex items-center space-x-3 mt-4 lg:mt-0">
            <button className="flex items-center space-x-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
              <Download size={18} />
              <span>Export pour IA</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
              <Globe size={18} />
              <span>Sync TripAdvisor</span>
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
            >
              <Plus size={18} />
              <span>Nouvelle Entrée</span>
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Sélecteur de Destination - Maintenant horizontal */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Destinations</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {destinations.map((destination) => (
              <div
                key={destination.id}
                onClick={() => setSelectedDestination(destination.id)}
                className={`cursor-pointer border rounded-lg p-4 transition-all ${
                  selectedDestination === destination.id
                    ? 'border-yellow-500 bg-yellow-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="aspect-video mb-3 rounded-lg overflow-hidden">
                  <img 
                    src={destination.image} 
                    alt={destination.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="font-medium text-gray-800">{destination.name}</h4>
                <p className="text-sm text-gray-600 mb-2">{destination.location}</p>
                
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-1">
                    <Star className="text-yellow-500" size={14} />
                    <span className="font-medium">{destination.rating}</span>
                  </div>
                  <span className="text-gray-500">{destination.entries} entrées</span>
                </div>
                
                <div className="text-xs text-gray-500 mt-2">
                  Mis à jour: {destination.lastUpdate}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contenu Principal - Maintenant pleine largeur */}
        <div className="space-y-6">
          {/* Header Destination */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-800">{currentDestination?.name}</h2>
                <p className="text-gray-600">{currentDestination?.location}</p>
              </div>
              <div className="flex items-center space-x-2">
                <button className="flex items-center space-x-2 px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                  <MapPin size={16} />
                  <span>Vue Carte</span>
                </button>
                <button className="flex items-center space-x-2 px-3 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50">
                  <BarChart3 size={16} />
                  <span>Analytics</span>
                </button>
              </div>
            </div>

            {/* Onglets */}
            <div className="border-b border-gray-200">
              <nav className="flex space-x-8">
                {[
                  { id: 'restaurants', label: 'Restaurants' },
                  { id: 'beaches', label: 'Plages' },
                  { id: 'activities', label: 'Activités' },
                  { id: 'shopping', label: 'Shopping' },
                  { id: 'culture', label: 'Culture' },
                  { id: 'transport', label: 'Transport' },
                  { id: 'yachtRoutes', label: 'Itinéraires Yacht' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-yellow-500 text-yellow-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {getTabIcon(tab.id)}
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <AddEntryForm />

          {/* Contenu des Onglets */}
          <div className="space-y-4">
            {activeTab === 'yachtRoutes' && currentKnowledge.yachtRoutes ? (
              // Onglet spécial pour les itinéraires yacht
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Itinéraires Yacht Recommandés</h3>
                
                <div className="mb-6">
                  <div className="bg-gray-100 rounded-lg p-4 text-center">
                    <MapPin size={48} className="mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600">Carte interactive - Itinéraires yacht</p>
                    <p className="text-sm text-gray-500">Intégration cartographique à implémenter</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {currentKnowledge.yachtRoutes.map((route) => (
                    <div key={route.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-800">{route.name}</h4>
                        <span className="text-sm text-gray-600">{route.duration}</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                        <div>
                          <span className="text-gray-600">Distance:</span>
                          <span className="font-medium text-gray-800 ml-1">{route.distance}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Difficulté:</span>
                          <span className="font-medium text-gray-800 ml-1">{route.difficulty}</span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-600">Meilleure période:</span>
                          <span className="font-medium text-gray-800 ml-1">{route.bestTime}</span>
                        </div>
                      </div>
                      
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Points d'intérêt:</p>
                        <div className="flex flex-wrap gap-2">
                          {route.highlights.map((highlight, index) => (
                            <span key={index} className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                              {highlight}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              // Onglets standard avec entrées
              <>
                {currentEntries.map((entry) => (
                  <div key={entry.id} className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          {getCategoryIcon(entry.category)}
                          <h3 className="text-lg font-semibold text-gray-800">{entry.name}</h3>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getCategoryColor(entry.category)}`}>
                            {entry.category}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-4 mb-3">
                          <div className="flex items-center space-x-1">
                            <Star className="text-yellow-500" size={16} />
                            <span className="font-medium">{entry.rating}</span>
                          </div>
                          <span className="text-sm text-gray-600">{entry.type}</span>
                          <span className="text-sm font-medium text-green-600">{entry.priceRange}</span>
                          <div className="flex items-center space-x-1">
                            <MapPin className="text-gray-400" size={14} />
                            <span className="text-sm text-gray-600">{entry.location}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button className="text-gray-400 hover:text-gray-600">
                          <Eye size={16} />
                        </button>
                        <button className="text-gray-400 hover:text-gray-600">
                          <Edit size={16} />
                        </button>
                        <button className="text-gray-400 hover:text-red-600">
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <h4 className="font-medium text-gray-700 mb-1">Spécialités</h4>
                        <p className="text-sm text-gray-600">{entry.specialties}</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-700 mb-1">Avis Clients</h4>
                        <p className="text-sm text-gray-600">"{entry.clientReviews}"</p>
                      </div>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
                      <h4 className="font-medium text-yellow-800 mb-1">Notes PRIVIA</h4>
                      <p className="text-sm text-yellow-700">{entry.ourNotes}</p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {entry.tags.map((tag, index) => (
                        <span key={index} className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}

                {currentEntries.length === 0 && (
                  <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                    <div className="text-gray-400 mb-4">
                      {getTabIcon(activeTab)}
                    </div>
                    <h3 className="text-lg font-medium text-gray-600 mb-2">Aucune entrée dans cette catégorie</h3>
                    <p className="text-gray-500 mb-4">Commencez par ajouter des recommandations pour cette destination</p>
                    <button
                      onClick={() => setShowAddForm(true)}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700"
                    >
                      Ajouter une entrée
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Panneau IA - Maintenant en bas */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Suggestions IA */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center space-x-2 mb-4">
              <Zap className="text-purple-600" size={20} />
              <h3 className="text-lg font-semibold text-gray-800">Assistant IA</h3>
            </div>
            
            <div className="space-y-4">
              {aiSuggestions.map((suggestion, index) => (
                <div key={index} className={`border rounded-lg p-4 ${
                  suggestion.type === 'urgent' ? 'border-red-200 bg-red-50' :
                  suggestion.type === 'opportunity' ? 'border-green-200 bg-green-50' :
                  'border-blue-200 bg-blue-50'
                }`}>
                  <h4 className={`font-medium mb-2 ${
                    suggestion.type === 'urgent' ? 'text-red-800' :
                    suggestion.type === 'opportunity' ? 'text-green-800' :
                    'text-blue-800'
                  }`}>
                    {suggestion.title}
                  </h4>
                  <p className={`text-sm mb-3 ${
                    suggestion.type === 'urgent' ? 'text-red-700' :
                    suggestion.type === 'opportunity' ? 'text-green-700' :
                    'text-blue-700'
                  }`}>
                    {suggestion.content}
                  </p>
                  <button className={`text-sm font-medium ${
                    suggestion.type === 'urgent' ? 'text-red-600 hover:text-red-700' :
                    suggestion.type === 'opportunity' ? 'text-green-600 hover:text-green-700' :
                    'text-blue-600 hover:text-blue-700'
                  }`}>
                    {suggestion.action} →
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* État des Connexions API */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Connexions API</h3>
            
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">TripAdvisor</span>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-green-600">Actif</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Google Places</span>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-green-600">Actif</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Yelp</span>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm text-yellow-600">Limité</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">OpenTable</span>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-sm text-red-600">Erreur</span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions Rapides */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Actions Rapides</h3>
            
            <div className="space-y-2">
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
                📊 Générer rapport destination
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
                🔄 Synchroniser toutes les données
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
                📧 Envoyer recommandations client
              </button>
              <button className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded">
                🎯 Analyser tendances
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DestinationKnowledge;